import type { ApolloClient } from "@apollo/client";
import { gql } from "@/gql";
import type { AssetKind } from "@/gql/graphql";

const GENERATE_ASSET_UPLOAD_URL = gql(`
  mutation GenerateAssetUploadUrlForHelper($input: GenerateAssetUploadUrlInput!) {
    generateAssetUploadUrl(input: $input) {
      uploadUrl
      storageKey
      expiresIn
    }
  }
`);

const REMOVE_ASSET = gql(`
  mutation RemoveAssetForHelper($input: RemoveAssetInput!) {
    removeAsset(input: $input)
  }
`);

export interface UploadAssetParams {
  file: File;
  kind: AssetKind;
  salonId: string;
}

export interface UploadAssetResult {
  storageKey: string;
}

export async function uploadAsset(
  apolloClient: ApolloClient<unknown>,
  { file, kind, salonId }: UploadAssetParams
): Promise<UploadAssetResult> {
  const { data, errors } = await apolloClient.mutate({
    mutation: GENERATE_ASSET_UPLOAD_URL,
    variables: { input: { kind, salonId, contentType: file.type } },
  });

  if (errors?.length) {
    const err = errors[0];
    const fieldErrors = (err.extensions as { fieldErrors?: Record<string, string> } | undefined)
      ?.fieldErrors;
    const detail = fieldErrors ? ` (${JSON.stringify(fieldErrors)})` : "";
    throw new Error(`${err.message}${detail}`);
  }

  const uploadInfo = data?.generateAssetUploadUrl;
  if (!uploadInfo) {
    throw new Error("Failed to generate upload URL");
  }

  const putResponse = await fetch(uploadInfo.uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });

  if (!putResponse.ok) {
    throw new Error(`Upload failed with status ${putResponse.status}`);
  }

  return { storageKey: uploadInfo.storageKey };
}

export interface RemoveAssetParams {
  kind: AssetKind;
  salonId: string;
  storageKey: string;
}

export async function removeAsset(
  apolloClient: ApolloClient<unknown>,
  { kind, salonId, storageKey }: RemoveAssetParams
): Promise<void> {
  const { errors } = await apolloClient.mutate({
    mutation: REMOVE_ASSET,
    variables: { input: { kind, salonId, storageKey } },
  });

  if (errors?.length) {
    throw new Error(errors[0].message);
  }
}
